/**
 * ENG-8201 (Slice 1): how Claude Code names a project's transcript directory.
 *
 * Claude Code stores every session transcript under
 * `~/.claude/projects/<encoded-cwd>/<sessionId>.jsonl`, where `<encoded-cwd>` is
 * the agent's cwd with its separators flattened. The encoder lived privately in
 * the manager (apps/cli's daily-session.ts); it moves here because the channel
 * MCP servers now need to locate the same directory to watch for a refusal
 * (ENG-8201 Slice 2). Two copies of this rule would be a silent divergence: the
 * MCP would watch a directory that does not exist and simply never report a
 * cap — indistinguishable, from the outside, from an agent that is fine.
 *
 * Pure string manipulation, no `node:path` and no `node:fs`, so core stays
 * browser-safe. Each consumer joins it onto its own home dir.
 */

/**
 * Encode an absolute project dir the way Claude Code stores it under
 * `~/.claude/projects/`. Claude collapses runs of `/` and `.` into single `-`
 * separators with a leading `-` (no separator at the start). An earlier
 * "/ only" encoder produced a stale path for any project dir containing a `.`
 * (e.g. `/root/.augmented/scout/project`), which made the manager's
 * session-file-exists check return false even when the JSONL was on disk.
 *
 * Diagnosed live on prod scout (ENG-4659): the on-disk dir was
 *   /root/.claude/projects/-root--augmented-scout-project/
 * but the encoder produced
 *   /root/.claude/projects/-root-.augmented-scout-project/
 * — the dot in `.augmented` wasn't translated. Result: every "is there JSONL on
 * disk" check returned false, the manager fell back to `--session-id` reuse, and
 * Claude rejected the same UUID with "Session ID already in use" forever.
 *
 * Empirical observations from `/root/.claude/projects/` on a live host:
 *   /usr/bin                        -> -usr-bin
 *   /root/.augmented/scout/project  -> -root--augmented-scout-project
 * Behaviour: `[/.]` -> `-`, with consecutive separators preserved (the `/.`
 * between `root/` and `.augmented` becomes `--`).
 */
export function encodeClaudeProjectPath(projectDir: string): string {
  return '-' + projectDir.replace(/^\//, '').replace(/[/.]/g, '-');
}
